'use client';

import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, RefreshCw, Trash2, ExternalLink, Eye } from 'lucide-react';
import { Stock } from '@/types';
import { formatNumber } from '@/lib/formatNumber';
import { cn } from '@/lib/cn';

interface StockCardProps {
  stock: Stock;
  currency: 'USD' | 'CAD';
  exchangeRate: number;
  convert: (val: number | null | undefined) => number;
  onRefresh: (id: string, symbol: string) => void;
  onSelect: (stock: Stock) => void;
  onDelete: (id: string) => void;
  index?: number;
  isWatchlist?: boolean;
}

export const StockCard: React.FC<StockCardProps> = ({ stock, currency, exchangeRate, convert, onRefresh, onSelect, onDelete, index = 0, isWatchlist }) => {
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const totalShares = stock.lots.reduce((acc, lot) => acc + lot.shares, 0);

  const totalCostUSD = stock.lots.reduce((acc, lot) => {
    const lotCurrency = lot.currency || 'USD';
    const cost = lot.shares * lot.price;
    return acc + (lotCurrency === 'CAD' ? cost / exchangeRate : cost);
  }, 0);

  const priceInUSD = (stock.currency === 'CAD' && stock.currentPrice)
    ? stock.currentPrice / exchangeRate
    : (stock.currentPrice || 0);

  const currentValueUSD = priceInUSD * totalShares;
  const profitUSD = currentValueUSD - totalCostUSD;
  const profitPercent = totalCostUSD > 0 ? (profitUSD / totalCostUSD) * 100 : 0;
  const isProfit = profitUSD >= 0;

  const displayPrice = convert(priceInUSD);
  const displayProfit = convert(profitUSD);
  const currencySymbol = currency === 'USD' ? '$' : 'C$';

  // For watchlist stocks, compute daily price change from historical data
  const dailyChange = (() => {
    if (!isWatchlist || !stock.currentPrice || stock.historicalData.length < 2) return null;
    const sorted = [...stock.historicalData].sort((a, b) => b.date.localeCompare(a.date));
    const prevPrice = sorted[1]?.price;
    if (!prevPrice) return null;
    const changePct = ((stock.currentPrice - prevPrice) / prevPrice) * 100;
    return { percent: changePct, isUp: changePct >= 0 };
  })();

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;

    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const deltaX = touchX - touchStartX.current;
    const deltaY = touchY - touchStartY.current;

    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      return;
    }

    if (deltaX < 0) {
      setSwipeX(Math.max(deltaX, -120));
    } else {
      setSwipeX(0);
    }
  };

  const handleTouchEnd = () => {
    setIsSwiping(false);
    if (swipeX < -60) {
      setSwipeX(-120);
    } else {
      setSwipeX(0);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${stock.symbol}?\n\nThis will remove the asset and all associated lots from your portfolio.`
    );
    if (confirmDelete) {
      onDelete(stock.id);
      setSwipeX(0);
    }
  };

  const handleRefreshClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRefresh(stock.id, stock.symbol);
    setSwipeX(0);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{ y: -2 }}
      className="relative overflow-hidden rounded-2xl"
      style={{ willChange: 'transform' }}
    >
      {/* Action buttons behind the card (revealed on swipe left on mobile) */}
      <div className={cn(
        "absolute right-0 top-0 bottom-0 flex items-center gap-2 pr-4 sm:hidden",
        swipeX >= 0 && "invisible"
      )}>
        <button
          onClick={handleRefreshClick}
          className="rounded-full p-3 text-white"
          style={{ backgroundColor: 'var(--color-accent)' }}
          title="Refresh Data"
        >
          <RefreshCw size={18} />
        </button>
        <button
          onClick={handleDelete}
          className="rounded-full bg-red-600 p-3 text-white hover:bg-red-700"
          title="Delete Stock"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Swipeable card content */}
      <div
        ref={cardRef}
        className="stock-card group relative p-5 rounded-2xl transition-all cursor-pointer"
        style={{
          backgroundColor: 'var(--color-surface)',
          boxShadow: 'var(--shadow-card)',
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease-out, box-shadow 0.2s ease',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => {
          if (swipeX < 0) {
            setSwipeX(0);
          } else {
            onSelect(stock);
          }
        }}
      >
      <div className="mb-4 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-[var(--color-text)]">{stock.symbol}</h3>
            {stock.isLoading && <RefreshCw className="animate-spin" size={14} style={{ color: 'var(--color-accent-muted)' }} />}
            {stock.exchange && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}>
                    {stock.exchange}
                </span>
            )}
          </div>
          <p className="text-sm truncate max-w-[150px]" style={{ color: 'var(--color-text-secondary)' }}>{stock.name || 'Unknown Company'}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-[var(--color-text)]">
            {stock.currentPrice ? `${currencySymbol}${formatNumber(displayPrice)}` : '---'}
          </p>
          {stock.currentPrice && !isWatchlist && (
             <div className={cn(
               "flex items-center justify-end gap-1 text-sm font-medium",
               isProfit ? 'text-green-400' : 'text-red-400'
             )}>
                {isProfit ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <span>{profitPercent >= 0 ? '+' : ''}{formatNumber(profitPercent)}%</span>
             </div>
          )}
          {stock.currentPrice && isWatchlist && dailyChange && (
             <div className={cn(
               "flex items-center justify-end gap-1 text-sm font-medium",
               dailyChange.isUp ? 'text-green-400' : 'text-red-400'
             )}>
                {dailyChange.isUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <span>{dailyChange.percent >= 0 ? '+' : ''}{formatNumber(dailyChange.percent)}%</span>
             </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4" style={{ borderTop: '1px solid var(--color-border-subtle)' }}>
        {isWatchlist ? (
          <>
            <div>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Daily Change</p>
              <p className={cn("font-semibold", dailyChange && dailyChange.isUp ? 'text-green-400' : dailyChange ? 'text-red-400' : '')} style={!dailyChange ? { color: 'var(--color-text-muted)' } : undefined}>
                {dailyChange ? `${dailyChange.percent >= 0 ? '+' : ''}${formatNumber(dailyChange.percent)}%` : '---'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Status</p>
              <p className="font-semibold flex items-center justify-end gap-1" style={{ color: 'var(--color-accent-muted)' }}>
                <Eye size={12} /> Watchlist
              </p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Your Shares</p>
              <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{formatNumber(totalShares)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Total Profit ({currency})</p>
              <p className={cn("font-semibold", isProfit ? 'text-green-400' : 'text-red-400')}>
                 {stock.currentPrice ? `${profitUSD > 0 ? '+' : ''}${currencySymbol}${formatNumber(displayProfit)}` : '---'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons (desktop only - top-right on hover, hidden on touch devices) */}
      <div className="stock-card-actions hidden sm:flex absolute top-4 right-4 gap-2 opacity-0 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onRefresh(stock.id, stock.symbol); }}
          className="rounded-full p-2 transition-colors"
          style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}
          title="Refresh Data"
        >
          <RefreshCw size={14} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); handleDelete(e); }}
          className="rounded-full p-2 transition-colors"
          style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-primary)' }}
          title="Delete Stock"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Grounding Source Indicator */}
      {stock.groundingUrls && stock.groundingUrls.length > 0 && (
         <div className="stock-card-grounding absolute bottom-2 left-4 opacity-0 transition-opacity">
            <a
              href={stock.groundingUrls[0]}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-[10px] hover:underline"
              style={{ color: 'var(--color-accent-muted)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={10} /> Source
            </a>
         </div>
      )}
      </div>
    </motion.div>
  );
};
